import React, { useEffect, useMemo, useState } from "react";
import { D, st } from "../../constants";
import { BREAKER_SIZES, VOLTAGES, CONTINUOUS_LOAD_FACTOR, LIGHTING_CONTROL_SYSTEMS } from "./avLightingRates";
import { exportPdf } from "./pdfExport";

let nextCircuitId = 1;
let nextModuleId = 1;
let nextLoadId = 1;

function newLoad() {
  return { id: nextLoadId++, desc: "", watts: "", continuous: true, dimmed: false };
}

function newCircuit(displayName) {
  const id = nextCircuitId++;
  return { id, name: displayName, breakerAmps: 20, voltage: 120 };
}

function newModule(n, circuitId) {
  return { id: nextModuleId++, name: `Module ${n}`, circuitId, system: "custom", loads: [newLoad()] };
}

function calcModule(module) {
  const loads = module.loads.filter((l) => l.desc.trim() || Number(l.watts) > 0);
  const continuousWatts = loads.filter((l) => l.continuous).reduce((s, l) => s + (Number(l.watts) || 0), 0);
  const nonContinuousWatts = loads.filter((l) => !l.continuous).reduce((s, l) => s + (Number(l.watts) || 0), 0);
  const totalWatts = continuousWatts + nonContinuousWatts;
  return { continuousWatts, nonContinuousWatts, totalWatts };
}

function calcCircuit(circuit, modules) {
  const assigned = modules.filter((m) => m.circuitId === circuit.id).map((m) => ({ module: m, calc: calcModule(m) }));
  const continuousWatts = assigned.reduce((s, a) => s + a.calc.continuousWatts, 0);
  const nonContinuousWatts = assigned.reduce((s, a) => s + a.calc.nonContinuousWatts, 0);
  const totalWatts = continuousWatts + nonContinuousWatts;
  const voltage = Number(circuit.voltage) || 120;
  const breakerAmps = Number(circuit.breakerAmps) || 0;
  const requiredAmps = (nonContinuousWatts + continuousWatts * 1.25) / voltage;
  const breakerOk = breakerAmps >= requiredAmps;
  const maxContinuousWattsAllowed = breakerAmps * voltage * CONTINUOUS_LOAD_FACTOR;
  return { assigned, continuousWatts, nonContinuousWatts, totalWatts, requiredAmps, breakerOk, maxContinuousWattsAllowed };
}

const printTh = { textAlign: "left", padding: "3px 6px", borderBottom: "1px solid #999", color: "#000", fontSize: 11 };
const printTd = { padding: "3px 6px", borderBottom: "1px solid #ddd", color: "#000", fontSize: 11 };

function PrintView({ jobName, jobAddress, circuitCalcs }) {
  return (
    <div className="print-only" style={{ background: "#fff", color: "#000", padding: 24, fontFamily: "sans-serif" }}>
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Lighting Load Schedule</h1>
      {jobName && <div style={{ fontSize: 14, fontWeight: 600 }}>{jobName}</div>}
      {jobAddress && <div style={{ fontSize: 13, color: "#444", marginBottom: 12 }}>{jobAddress}</div>}
      <div style={{ fontSize: 11, color: "#666", marginBottom: 20 }}>Printed {new Date().toLocaleDateString()}</div>

      {circuitCalcs.map(({ circuit, calc }) => (
        <div key={circuit.id} style={{ marginBottom: 24, breakInside: "avoid" }}>
          <div style={{ fontSize: 14, fontWeight: 700, borderBottom: "2px solid #000", paddingBottom: 4, marginBottom: 8 }}>
            {circuit.name} — {circuit.breakerAmps}A / {circuit.voltage}V
          </div>
          {calc.assigned.map(({ module, calc: mCalc }) => (
            <div key={module.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{module.name}</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={printTh}>Load</th>
                    <th style={printTh}>Watts</th>
                    <th style={printTh}>Continuous</th>
                    <th style={printTh}>Dimmed</th>
                  </tr>
                </thead>
                <tbody>
                  {module.loads.filter((l) => l.desc.trim() || Number(l.watts) > 0).map((l) => (
                    <tr key={l.id}>
                      <td style={printTd}>{l.desc}</td>
                      <td style={printTd}>{l.watts}W</td>
                      <td style={printTd}>{l.continuous ? "Yes" : "No"}</td>
                      <td style={printTd}>{l.dimmed ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600 }}>
            Circuit total: {calc.totalWatts.toFixed(0)}W ({calc.continuousWatts.toFixed(0)}W continuous) — requires {calc.requiredAmps.toFixed(1)}A minimum —{" "}
            {calc.breakerOk ? `OK on ${circuit.breakerAmps}A breaker` : `EXCEEDS ${circuit.breakerAmps}A breaker — resize or split`}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LoadSchedule({ initialData, onChange }) {
  const [jobName, setJobName] = useState(initialData?.jobName || "");
  const [jobAddress, setJobAddress] = useState(initialData?.jobAddress || "");
  const [circuits, setCircuits] = useState(() => initialData?.circuits?.length ? initialData.circuits : [newCircuit("Circuit 1")]);
  const [modules, setModules] = useState(() => initialData?.modules?.length ? initialData.modules : [newModule(1, 1)]);

  const addCircuit = (name) => {
    const id = nextCircuitId++;
    let createdId = id;
    setCircuits((prev) => [...prev, { id, name: name || `Circuit ${prev.length + 1}`, breakerAmps: 20, voltage: 120 }]);
    return createdId;
  };
  const updateCircuit = (id, patch) => setCircuits((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeCircuit = (id) => setCircuits((prev) => prev.filter((c) => c.id !== id));

  const addModule = () => {
    const circuitId = circuits[0]?.id ?? addCircuit();
    setModules((prev) => [...prev, newModule(prev.length + 1, circuitId)]);
  };
  const removeModule = (id) => setModules((prev) => prev.filter((m) => m.id !== id));
  const updateModule = (id, patch) => setModules((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const addLoad = (moduleId) => setModules((prev) => prev.map((m) => (m.id === moduleId ? { ...m, loads: [...m.loads, newLoad()] } : m)));
  const removeLoad = (moduleId, loadId) =>
    setModules((prev) => prev.map((m) => (m.id === moduleId ? { ...m, loads: m.loads.filter((l) => l.id !== loadId) } : m)));
  const updateLoad = (moduleId, loadId, patch) =>
    setModules((prev) =>
      prev.map((m) => (m.id === moduleId ? { ...m, loads: m.loads.map((l) => (l.id === loadId ? { ...l, ...patch } : l)) } : m))
    );

  const handleSystemSelect = (moduleId, systemId) => {
    const system = LIGHTING_CONTROL_SYSTEMS.find((s) => s.id === systemId) || LIGHTING_CONTROL_SYSTEMS[0];
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        let loads = m.loads;
        if (system.loadsPerModule != null) {
          const target = system.loadsPerModule;
          if (loads.length < target) loads = [...loads, ...Array.from({ length: target - loads.length }, () => newLoad())];
          else if (loads.length > target) loads = loads.slice(0, target);
        }
        return { ...m, system: systemId, loads };
      })
    );
  };

  useEffect(() => {
    onChange?.({ jobName, jobAddress, circuits, modules });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobName, jobAddress, circuits, modules]);

  const circuitCalcs = useMemo(() => circuits.map((c) => ({ circuit: c, calc: calcCircuit(c, modules) })), [circuits, modules]);

  const handleExportPdf = () => {
    const sections = circuitCalcs
      .filter(({ calc }) => calc.assigned.length > 0)
      .map(({ circuit, calc }) => {
        const rows = [];
        calc.assigned.forEach(({ module }) => {
          module.loads
            .filter((l) => l.desc.trim() || Number(l.watts) > 0)
            .forEach((l) => {
              rows.push([module.name, l.desc, l.watts + "W", l.continuous ? "Yes" : "No", l.dimmed ? "Yes" : "No"]);
            });
        });
        return {
          heading: circuit.name + " — " + circuit.breakerAmps + "A / " + circuit.voltage + "V",
          columns: ["Module", "Load", "Watts", "Continuous", "Dimmed"],
          rows,
          footer:
            "Circuit total: " + calc.totalWatts.toFixed(0) + "W (" + calc.continuousWatts.toFixed(0) + "W continuous) — requires " +
            calc.requiredAmps.toFixed(1) + "A minimum — " +
            (calc.breakerOk ? "OK on " + circuit.breakerAmps + "A breaker" : "EXCEEDS " + circuit.breakerAmps + "A breaker — resize or split"),
        };
      });

    exportPdf({
      title: "Lighting Load Schedule",
      subtitle: jobName || undefined,
      meta: [jobAddress, "Printed " + new Date().toLocaleDateString()].filter(Boolean),
      sections,
      filename: jobName ? jobName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() + "-load-schedule.pdf" : "load-schedule.pdf",
    });
  };
  const circuitById = (id) => circuits.find((c) => c.id === id);

  const handleCircuitSelect = (moduleId, value) => {
    if (value === "__new__") {
      const id = addCircuit();
      updateModule(moduleId, { circuitId: id });
    } else {
      updateModule(moduleId, { circuitId: Number(value) });
    }
  };

  return (
    <div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
        }
        .print-only { display: none; }
      `}</style>

      <div className="no-print" style={st.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={st.h2}>Load Schedule</div>
            <div style={{ fontSize: 13, color: D.muted, marginBottom: 12 }}>
              Each Module is a dimmer/relay module with its own loads. Pick a control system to auto-populate the
              module's load count, assign it to a Circuit — multiple Modules can share one Circuit, and the NEC
              breaker check (210.19(A)(1)/210.20(A)) below adds up everything on that Circuit together.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <label style={{ display: "block", flex: 1, minWidth: 200 }}>
                <span style={st.label}>Residence name</span>
                <input value={jobName} onChange={(e) => setJobName(e.target.value)} style={st.input} placeholder="e.g. The Smith Residence" />
              </label>
              <label style={{ display: "block", flex: 1, minWidth: 200 }}>
                <span style={st.label}>Address</span>
                <input value={jobAddress} onChange={(e) => setJobAddress(e.target.value)} style={st.input} placeholder="123 Main St, City, FL" />
              </label>
            </div>
          </div>
          <div className="no-print" style={{ display: "flex", gap: 8 }}>
            <button style={st.btn()} onClick={handleExportPdf}>⬇ Export PDF</button>
            <button style={st.btn("primary")} onClick={() => window.print()}>🖨 Print Schedule</button>
          </div>
        </div>
      </div>

      <div className="no-print">
        {modules.map((module) => {
          const circuit = circuitById(module.circuitId);
          const system = LIGHTING_CONTROL_SYSTEMS.find((s) => s.id === module.system) || LIGHTING_CONTROL_SYSTEMS[0];
          return (
            <div key={module.id} style={st.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                <input
                  value={module.name}
                  onChange={(e) => updateModule(module.id, { name: e.target.value })}
                  style={{ ...st.input, fontWeight: 600, fontSize: 15, width: 200 }}
                />
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: D.muted }}>
                    System
                    <select value={module.system} onChange={(e) => handleSystemSelect(module.id, e.target.value)} style={{ ...st.input, width: 190 }}>
                      {LIGHTING_CONTROL_SYSTEMS.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}{s.loadsPerModule ? ` (${s.loadsPerModule} loads)` : ""}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: D.muted }}>
                    Circuit
                    <select value={module.circuitId} onChange={(e) => handleCircuitSelect(module.id, e.target.value)} style={{ ...st.input, width: 130 }}>
                      {circuits.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      <option value="__new__">+ Add New Circuit</option>
                    </select>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: D.muted }}>
                    Breaker
                    <select
                      value={circuit?.breakerAmps ?? 20}
                      onChange={(e) => circuit && updateCircuit(circuit.id, { breakerAmps: e.target.value })}
                      style={{ ...st.input, width: 80 }}
                    >
                      {BREAKER_SIZES.map((a) => <option key={a} value={a}>{a}A</option>)}
                    </select>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: D.muted }}>
                    Voltage
                    <select
                      value={circuit?.voltage ?? 120}
                      onChange={(e) => circuit && updateCircuit(circuit.id, { voltage: e.target.value })}
                      style={{ ...st.input, width: 80 }}
                    >
                      {VOLTAGES.map((v) => <option key={v} value={v}>{v}V</option>)}
                    </select>
                  </label>
                  {modules.length > 1 && <button style={st.btn("danger")} onClick={() => removeModule(module.id)}>Remove</button>}
                </div>
              </div>

              {system.loadsPerModule != null && (
                <div style={{ fontSize: 11, color: D.muted, marginBottom: 10 }}>
                  {system.label} modules control {system.loadsPerModule} loads — rows below auto-adjusted to match. {system.source}
                </div>
              )}

              <table style={{ ...st.table, marginBottom: 10 }}>
                <thead>
                  <tr>
                    <th style={st.th}>Load description</th>
                    <th style={st.th}>Watts</th>
                    <th style={st.th}>Continuous?</th>
                    <th style={st.th}>Dimmed?</th>
                    <th style={st.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {module.loads.map((load) => (
                    <tr key={load.id}>
                      <td style={st.td}>
                        <input value={load.desc} onChange={(e) => updateLoad(module.id, load.id, { desc: e.target.value })} style={st.input} placeholder="e.g. Recessed cans, kitchen island" />
                      </td>
                      <td style={st.td}>
                        <input type="number" min="0" value={load.watts} onChange={(e) => updateLoad(module.id, load.id, { watts: e.target.value })} style={{ ...st.input, width: 90 }} />
                      </td>
                      <td style={st.td}>
                        <input type="checkbox" checked={load.continuous} onChange={(e) => updateLoad(module.id, load.id, { continuous: e.target.checked })} style={{ accentColor: D.accent }} />
                      </td>
                      <td style={st.td}>
                        <input type="checkbox" checked={load.dimmed} onChange={(e) => updateLoad(module.id, load.id, { dimmed: e.target.checked })} style={{ accentColor: D.accent }} />
                      </td>
                      <td style={st.td}>
                        <button style={{ ...st.btn("danger"), fontSize: 11, padding: "3px 8px" }} onClick={() => removeLoad(module.id, load.id)}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button style={st.btn()} onClick={() => addLoad(module.id)}>+ Add load</button>
            </div>
          );
        })}

        <button style={st.btn("primary")} onClick={addModule}>+ Add module</button>

        <div style={{ ...st.h2, marginTop: 28 }}>Circuit Summary</div>
        {circuitCalcs.map(({ circuit, calc }) => (
          <div key={circuit.id} style={st.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <input
                value={circuit.name}
                onChange={(e) => updateCircuit(circuit.id, { name: e.target.value })}
                style={{ ...st.input, fontWeight: 600, width: 160 }}
              />
              <div style={{ fontSize: 12, color: D.muted }}>
                {circuit.breakerAmps}A / {circuit.voltage}V — {calc.assigned.length} module{calc.assigned.length === 1 ? "" : "s"}
                {calc.assigned.length === 0 && (
                  <button style={{ ...st.btn("danger"), marginLeft: 10, fontSize: 11, padding: "3px 8px" }} onClick={() => removeCircuit(circuit.id)}>Remove circuit</button>
                )}
              </div>
            </div>
            {calc.assigned.length > 0 && (
              <div style={{ fontSize: 12, color: D.muted, marginBottom: 8 }}>{calc.assigned.map((a) => a.module.name).join(", ")}</div>
            )}
            <div style={{ padding: 12, borderRadius: 8, background: calc.breakerOk ? D.card : D.danger + "1a", border: `1px solid ${calc.breakerOk ? D.border : D.danger + "55"}`, fontSize: 12, color: D.muted }}>
              Total: <strong style={{ color: D.text }}>{calc.totalWatts.toFixed(0)}W</strong>
              {" "}({calc.continuousWatts.toFixed(0)}W continuous, {calc.nonContinuousWatts.toFixed(0)}W non-continuous)
              {" — "}
              requires <strong style={{ color: D.text }}>{calc.requiredAmps.toFixed(1)}A</strong> minimum breaker (NEC 210.19(A)(1))
              {" — "}
              max continuous load this breaker allows: <strong style={{ color: D.text }}>{calc.maxContinuousWattsAllowed.toFixed(0)}W</strong>
              {!calc.breakerOk && (
                <div style={{ color: D.danger, fontWeight: 600, marginTop: 4 }}>
                  {circuit.breakerAmps}A breaker is undersized for this circuit's combined load — upsize the breaker or move a module to another circuit.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <PrintView jobName={jobName} jobAddress={jobAddress} circuitCalcs={circuitCalcs} />
    </div>
  );
}
